// api/inboxThreads — thread-level state for the unified inbox, built against lane C's
// contract (per Khalis, 2026-10-22): list rows carry `archived_at` / `handled_at` /
// `handled_by`; the list takes `?archived=false|true|all`; `intent="automated"` marks
// bot / no-reply threads. Today's rows still carry `is_archived`, so the readers accept
// both; nothing here breaks before C's leg lands.
//
// Write paths — ONE place, so a path change is one edit (lane C, crm-mk2
// lane/c-comms-conversation-and-followup: 9bbe02a4 archive pair, 170c96df handled,
// 75a73abd link; each view accepts POST and PATCH):
//   POST /comms/threads/<key>/archive/  {archived: bool}
//   POST /comms/threads/<key>/handled/  {handled: bool}     → {handled_at, handled_by, handled_by_name}
//   POST /comms/threads/<key>/link/     {customer_id} | {lead_id} — exactly one
//        → {success, conversation_key, …}: the conversation is re-keyed by the party it
//        now belongs to, so the caller must carry on with the NEW conversation_key.
//        Office roles only (403 for a technician). Feature-detected: see linkAvailable.
// <key> is thread_id, else party_universal_id (every thread view matches either).
import api from "./client";

export const threadKey = (row) => String(row?.thread_id || row?.party_universal_id || "").trim();
export const isArchived = (row) => Boolean(row?.archived_at || row?.is_archived);
export const isHandled = (row) => Boolean(row?.handled_at);
export const isLinked = (row) => Boolean(row?.customer_id || row?.lead_id);

// The backend stamp first (exact), then the classic bot markers in the intent string.
const AUTOMATED_RE = /(^|_)(automated|notification|auto[_-]?reply|system|first[_-]?touch)/;
export const isAutomatedIntent = (intent) => AUTOMATED_RE.test(String(intent || "").toLowerCase());

export const archiveThread = async (row, archived = true) => {
  const key = threadKey(row);
  if (!key) throw new Error("This conversation has no thread id yet.");
  const res = await api.post(`/comms/threads/${encodeURIComponent(key)}/archive/`, { archived });
  return res.data;
};

export const handleThread = async (row, handled = true) => {
  const key = threadKey(row);
  if (!key) throw new Error("This conversation has no thread id yet.");
  const res = await api.post(`/comms/threads/${encodeURIComponent(key)}/handled/`, { handled });
  return res.data;
};

// Resolves to {customer_id|lead_id, conversation_key?, party_universal_id?} — the ids
// to patch onto the row plus the new key the server now files the conversation under.
export const linkThread = async (row, target = {}) => {
  const key = threadKey(row);
  if (!key) throw new Error("This conversation has no thread id yet.");
  const ids = Object.fromEntries(
    Object.entries(target).filter(([k, v]) => (k === "customer_id" || k === "lead_id") && v != null && v !== "")
  );
  if (Object.keys(ids).length !== 1) throw new Error("Pick one: a customer or a lead.");
  const res = await api.post(`/comms/threads/${encodeURIComponent(key)}/link/`, ids);
  const d = res.data || {};
  return {
    ...ids,
    ...(d.conversation_key ? { conversation_key: d.conversation_key } : {}),
    ...(d.party_universal_id ? { party_universal_id: d.party_universal_id } : {}),
  };
};

// Is the link route live on this API? Until C's leg deploys the route 404s, and a
// "Link to customer" button whose request 404s is worse than no button. OPTIONS is
// a read: Django resolves the URL before any view runs, so a 404 means "no route"
// and any other status (200, 401, 403, 405) means it exists. Asked once per load.
let linkProbe = null;
export const linkAvailable = () => {
  if (!linkProbe) {
    linkProbe = api
      .options("/comms/threads/_/link/")
      .then(() => true)
      .catch((e) => (e?.response?.status ? e.response.status !== 404 : false));
  }
  return linkProbe;
};
